import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Wallet, Play, Send, CheckCircle, Banknote, FileDown, Settings, Pencil, RefreshCw, FileText } from 'lucide-react';
import StatutoryRulesTab from '../components/payroll/StatutoryRulesTab';
import RemittancesTab from '../components/payroll/RemittancesTab';
import CompensationTrackerTab from '../components/payroll/CompensationTrackerTab';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString()}`;
const STATUS = { draft: 'bg-slate-100 text-slate-700', pending_approval: 'bg-blue-100 text-blue-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700', paid: 'bg-green-100 text-green-800' };
const th = 'px-2 py-2 text-left text-[10px] font-semibold uppercase bg-muted sticky top-0 whitespace-nowrap border-b';
const td = 'px-2 py-1.5 whitespace-nowrap border-b text-xs text-right tabular-nums';
const PROFILE_EMPTY = { payment_method: 'bank', bank_name: '', bank_branch: '', account_name: '', account_number: '', mobile_money_number: '', tin: '', nssf_number: '', resident_status: 'resident', basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowances: '', health_insurance_mode: 'none', health_insurance_amount: '', local_service_tax: '', other_deductions: '', payroll_status: 'active', change_reason: '' };

export default function SalariesPage() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [staff, setStaff] = useState([]);
  const [editStaff, setEditStaff] = useState(null);
  const [profile, setProfile] = useState(PROFILE_EMPTY);
  const [payOpen, setPayOpen] = useState(false);
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/payroll/runs`);
      setRuns(r.data.items || []);
      const cur = (r.data.items || []).find((x) => x.month === month && x.year === year);
      if (cur) {
        const d = await axios.get(`${API}/api/payroll/runs/${cur.id}`);
        setRun(d.data);
      } else setRun(null);
    } catch { toast.error('Failed to load payroll runs'); }
    axios.get(`${API}/api/payroll/staff`).then((r) => setStaff(r.data.items || [])).catch(() => {});
  }, [month, year]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };
  const generate = () => act(() => axios.post(`${API}/api/payroll/runs`, { month, year }), 'Payroll generated');
  const dl = async (fmt2) => {
    try {
      const r = await axios.get(`${API}/api/payroll/runs/${run.id}/${fmt2 === 'payslips' ? 'payslips' : `export?format=${fmt2}`}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `payroll_${run.period}_${fmt2}.${fmt2 === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
    } catch { toast.error('Export failed'); }
  };
  const dlPayslip = async (l) => {
    try {
      const r = await axios.get(`${API}/api/payroll/runs/${run.id}/payslips?staff_id=${l.staff_id}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `payslip_${run.period}_${l.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
    } catch { toast.error('Payslip download failed'); }
  };
  const openProfile = (s) => {
    setEditStaff(s);
    const p = s.profile || {};
    setProfile({ ...PROFILE_EMPTY, ...Object.fromEntries(Object.entries(p).filter(([k]) => k in PROFILE_EMPTY).map(([k, v]) => [k, v ?? ''])) });
  };
  const saveProfile = async (e) => {
    e.preventDefault();
    const num = (v) => parseFloat(v || 0);
    await act(() => axios.put(`${API}/api/payroll/profiles/${editStaff.staff_id}`, {
      ...profile, basic_salary: num(profile.basic_salary), housing_allowance: num(profile.housing_allowance),
      transport_allowance: num(profile.transport_allowance), other_allowances: num(profile.other_allowances),
      health_insurance_amount: num(profile.health_insurance_amount), local_service_tax: num(profile.local_service_tax),
      other_deductions: num(profile.other_deductions),
    }), 'Payroll profile saved');
    setEditStaff(null);
  };
  const t = run?.totals;
  const canApprove = run && run.created_by !== user?.id;

  return (
    <div className="space-y-5" data-testid="salaries-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><Wallet className="h-6 w-6 text-primary" /> Salaries / Payroll</h2>
          <p className="text-muted-foreground text-sm">Staff-linked payroll with Uganda PAYE & NSSF, approval workflow and Finance posting</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-36" data-testid="payroll-month"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 12 }, (_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{new Date(2000, i).toLocaleString('en', { month: 'long' })}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="payroll-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="payroll">
        <TabsList>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-payroll-employees">Employees & Salary Setup</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-payroll-history">Payroll History</TabsTrigger>
          <TabsTrigger value="tracker" data-testid="tab-comp-tracker">Compensation Tracker</TabsTrigger>
          <TabsTrigger value="remittances" data-testid="tab-payroll-remittances">Remittances</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-payroll-rules">Statutory Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="payroll" className="mt-4 space-y-3">
          {!run ? (
            <Card><CardContent className="py-10 text-center space-y-3">
              <p className="text-muted-foreground">No payroll for {year}-{String(month).padStart(2, '0')} yet.</p>
              <Button onClick={generate} data-testid="generate-payroll-btn"><Play className="h-4 w-4 mr-1" /> New Payroll (import staff & calculate)</Button>
            </CardContent></Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`capitalize ${STATUS[run.status]}`} variant="outline" data-testid="payroll-status">{run.status.replace(/_/g, ' ')}</Badge>
                <span className="text-sm text-muted-foreground">{run.employee_count} employees</span>
                {run.validation_warnings?.length > 0 && <Badge variant="outline" className="bg-amber-100 text-amber-800">⚠ {run.validation_warnings.length} warnings</Badge>}
                <div className="ml-auto flex flex-wrap gap-2">
                  {run.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(() => axios.post(`${API}/api/payroll/runs/${run.id}/recalculate`), 'Payroll recalculated from latest profiles')} data-testid="payroll-recalc-btn"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Recalculate</Button>}
                  {run.status === 'draft' && <Button size="sm" onClick={() => window.confirm('Submit payroll for approval?') && act(() => axios.post(`${API}/api/payroll/runs/${run.id}/submit`), 'Submitted for approval')} data-testid="payroll-submit-btn"><Send className="h-3.5 w-3.5 mr-1" /> Submit for Approval</Button>}
                  {run.status === 'pending_approval' && canApprove && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => window.confirm('Approve this payroll?') && act(() => axios.post(`${API}/api/payroll/runs/${run.id}/approve`), 'Payroll approved')} data-testid="payroll-approve-btn"><CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve</Button>}
                  {run.status === 'pending_approval' && canApprove && <Button size="sm" variant="destructive" onClick={() => { const r = window.prompt('Rejection reason'); if (r) act(() => axios.post(`${API}/api/payroll/runs/${run.id}/reject`, { reason: r }), 'Rejected'); }} data-testid="payroll-reject-btn">Reject</Button>}
                  {run.status === 'approved' && <Button size="sm" onClick={() => setPayOpen(true)} data-testid="payroll-pay-btn"><Banknote className="h-3.5 w-3.5 mr-1" /> Mark as Paid</Button>}
                  <Button size="sm" variant="outline" onClick={() => dl('excel')} data-testid="payroll-export-excel"><FileDown className="h-3.5 w-3.5 mr-1" /> Excel</Button>
                  <Button size="sm" variant="outline" onClick={() => dl('pdf')} data-testid="payroll-export-pdf">PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => dl('bank')} data-testid="payroll-export-bank">Bank Report</Button>
                  <Button size="sm" variant="outline" onClick={() => dl('payslips')} data-testid="payroll-payslips-btn">All Payslips</Button>
                </div>
              </div>
              {run.budget_check && (
                <div className={`text-xs rounded-md border p-2.5 ${run.budget_check.over_budget ? 'bg-red-50 border-red-300 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`} data-testid="payroll-budget-check">
                  <span className="font-semibold">{run.budget_check.over_budget ? '⚠ OVER PERSONNEL BUDGET: ' : '✓ Within Personnel budget: '}</span>
                  Budget {fmt(run.budget_check.budget)} · Spent to date {fmt(run.budget_check.actual_to_date)} · This payroll {fmt(run.budget_check.this_payroll)} → Projected {fmt(run.budget_check.projected)} ({run.budget_check.pct_after}% of budget{run.budget_check.over_budget ? `, ${fmt(Math.abs(run.budget_check.remaining_after))} over` : `, ${fmt(run.budget_check.remaining_after)} remaining`})
                </div>
              )}
              {t && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="payroll-kpis">
                  {[['Gross', t.gross_salary], ['PAYE', t.paye], ['Employee NSSF', t.employee_nssf], ['Employer NSSF', t.employer_nssf], ['Net Payroll', t.net_salary], ['Employer Cost', t.employer_cost]].map(([l, v]) => (
                    <Card key={l}><CardContent className="p-2.5"><p className="text-[10px] text-muted-foreground">{l}</p><p className="text-sm font-heading font-bold">{fmt(v)}</p></CardContent></Card>
                  ))}
                </div>
              )}
              <Card><CardContent className="p-0">
                <div className="overflow-auto max-h-[480px]" data-testid="payroll-table">
                  <table className="w-full min-w-[1400px] border-collapse">
                    <thead><tr>{['Employee', 'Dept', 'Position', 'Bank', 'Basic', 'Allowances', 'Gross', 'PAYE', 'NSSF 5%', 'Health', 'LST', 'Other', 'Total Ded.', 'Net Salary', 'NSSF 10%', 'Employer Cost', 'Status', 'Payslip'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {run.lines.map((l) => (
                        <tr key={l.staff_id} className="hover:bg-muted/40">
                          <td className={`${td} text-left font-medium sticky left-0 bg-background`}>{l.name}</td>
                          <td className={`${td} text-left`}>{l.department || '—'}</td>
                          <td className={`${td} text-left`}>{l.position || '—'}</td>
                          <td className={`${td} text-left`}>{l.bank_name || '—'}</td>
                          <td className={td}>{Number(l.basic_salary).toLocaleString()}</td>
                          <td className={td}>{Number(l.housing_allowance + l.transport_allowance + l.other_allowances).toLocaleString()}</td>
                          <td className={`${td} font-semibold`}>{Number(l.gross_salary).toLocaleString()}</td>
                          <td className={td}>{Number(l.paye).toLocaleString()}</td>
                          <td className={td}>{Number(l.employee_nssf).toLocaleString()}</td>
                          <td className={td}>{Number(l.health_insurance_employee).toLocaleString()}</td>
                          <td className={td}>{Number(l.local_service_tax).toLocaleString()}</td>
                          <td className={td}>{Number(l.other_deductions).toLocaleString()}</td>
                          <td className={td}>{Number(l.total_deductions).toLocaleString()}</td>
                          <td className={`${td} font-bold text-green-700`}>{Number(l.net_salary).toLocaleString()}</td>
                          <td className={td}>{Number(l.employer_nssf).toLocaleString()}</td>
                          <td className={td}>{Number(l.employer_cost).toLocaleString()}</td>
                          <td className={`${td} text-left`}><Badge variant="outline" className="capitalize text-[10px]">{l.payment_status}</Badge></td>
                          <td className={td}><Button variant="ghost" size="sm" className="h-6 px-1.5" title="Download payslip" onClick={() => dlPayslip(l)} data-testid={`payslip-btn-${l.staff_id}`}><FileText className="h-3.5 w-3.5" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent></Card>
              {run.validation_warnings?.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="payroll-warnings">
                  {run.validation_warnings.map((w, i) => <p key={`${w}-${i}`}>⚠ {w}</p>)}
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Staff Payroll Profiles</CardTitle>
              <CardDescription>Sourced from Personnel → Staff (master record). Configure salary & payment info to include in payroll.</CardDescription></CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead><tr>{['Employee', 'Position', 'Department', 'Basic Salary', 'Method', 'NSSF No.', 'Ready', ''].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.staff_id} className="hover:bg-muted/40" data-testid={`payroll-staff-${s.staff_id}`}>
                      <td className={`${td} text-left font-medium`}>{s.name}</td>
                      <td className={`${td} text-left`}>{s.position || '—'}</td>
                      <td className={`${td} text-left`}>{s.department || '—'}</td>
                      <td className={td}>{s.profile ? fmt(s.profile.basic_salary) : '—'}</td>
                      <td className={`${td} text-left capitalize`}>{s.profile?.payment_method?.replace(/_/g, ' ') || '—'}</td>
                      <td className={`${td} text-left`}>{s.profile?.nssf_number || '—'}</td>
                      <td className={`${td} text-left`}><Badge variant="outline" className={s.payroll_ready ? 'bg-green-100 text-green-800' : 'bg-slate-100'}>{s.payroll_ready ? 'Ready' : 'Not configured'}</Badge></td>
                      <td className={td}><Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openProfile(s)} data-testid={`edit-profile-${s.staff_id}`}><Pencil className="h-3.5 w-3.5" /></Button></td>
                    </tr>
                  ))}
                  {staff.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm">No staff in Personnel → Staff yet</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card><CardContent className="p-0">
            <table className="w-full">
              <thead><tr>{['Period', 'Employees', 'Gross', 'PAYE', 'NSSF', 'Net Payroll', 'Employer Cost', 'Status'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => { setMonth(r.month); setYear(r.year); }} data-testid={`history-${r.period}`}>
                    <td className={`${td} text-left font-medium`}>{r.period}</td>
                    <td className={td}>{r.employee_count}</td>
                    <td className={td}>{fmt(r.totals?.gross_salary)}</td>
                    <td className={td}>{fmt(r.totals?.paye)}</td>
                    <td className={td}>{fmt((r.totals?.employee_nssf || 0) + (r.totals?.employer_nssf || 0))}</td>
                    <td className={td}>{fmt(r.totals?.net_salary)}</td>
                    <td className={td}>{fmt(r.totals?.employer_cost)}</td>
                    <td className={`${td} text-left`}><Badge className={`capitalize ${STATUS[r.status]}`} variant="outline">{r.status.replace(/_/g, ' ')}</Badge></td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm">No payroll history yet</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="tracker" className="mt-4">
          <CompensationTrackerTab />
        </TabsContent>

        <TabsContent value="remittances" className="mt-4">
          <RemittancesTab />
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <StatutoryRulesTab />
        </TabsContent>
      </Tabs>

      {/* Profile dialog */}
      <Dialog open={!!editStaff} onOpenChange={(o) => { if (!o) setEditStaff(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Settings className="h-4 w-4" /> Payroll Profile — {editStaff?.name}</DialogTitle>
            <DialogDescription>Salary changes are kept in salary history (never overwritten)</DialogDescription></DialogHeader>
          <form onSubmit={saveProfile} className="grid grid-cols-2 gap-3">
            {[['basic_salary', 'Basic Salary (UGX) *'], ['housing_allowance', 'Housing Allowance'], ['transport_allowance', 'Transport Allowance'], ['other_allowances', 'Other Allowances'], ['health_insurance_amount', 'Health Insurance Amount'], ['local_service_tax', 'Local Service Tax'], ['other_deductions', 'Other Deductions']].map(([k, l]) => (
              <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
                <Input type="number" value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} data-testid={`profile-${k}`} /></div>
            ))}
            <div className="space-y-1"><Label className="text-xs">Health Insurance Mode</Label>
              <Select value={profile.health_insurance_mode} onValueChange={(v) => setProfile({ ...profile, health_insurance_mode: v })}>
                <SelectTrigger data-testid="profile-hi-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{['none', 'employer_paid', 'employee_deduction', 'shared'].map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Payment Method</Label>
              <Select value={profile.payment_method} onValueChange={(v) => setProfile({ ...profile, payment_method: v })}>
                <SelectTrigger data-testid="profile-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{['bank', 'mobile_money', 'cash'].map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Resident Status</Label>
              <Select value={profile.resident_status} onValueChange={(v) => setProfile({ ...profile, resident_status: v })}>
                <SelectTrigger data-testid="profile-resident"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="resident">Resident</SelectItem><SelectItem value="non_resident">Non-resident</SelectItem></SelectContent>
              </Select></div>
            {[['bank_name', 'Bank Name'], ['bank_branch', 'Bank Branch'], ['account_name', 'Account Name'], ['account_number', 'Account Number'], ['mobile_money_number', 'Mobile Money No.'], ['tin', 'TIN'], ['nssf_number', 'NSSF Number'], ['change_reason', 'Change Reason (if salary changed)']].map(([k, l]) => (
              <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
                <Input value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} data-testid={`profile-${k}`} /></div>
            ))}
            <div className="space-y-1"><Label className="text-xs">Payroll Status</Label>
              <Select value={profile.payroll_status} onValueChange={(v) => setProfile({ ...profile, payroll_status: v })}>
                <SelectTrigger data-testid="profile-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active (include in payroll)</SelectItem><SelectItem value="excluded">Excluded</SelectItem></SelectContent>
              </Select></div>
            {editStaff?.profile?.salary_history?.length > 0 && (
              <div className="col-span-2 border rounded-md p-2 bg-muted/30" data-testid="salary-history">
                <p className="text-xs font-semibold mb-1">Salary History</p>
                <table className="w-full text-xs">
                  <thead><tr>{['Date', 'Previous', 'New', 'Reason', 'Changed By'].map((h) => <th key={h} className="text-left px-1 py-0.5 text-[10px] uppercase text-muted-foreground">{h}</th>)}</tr></thead>
                  <tbody>
                    {[...editStaff.profile.salary_history].reverse().map((h, i) => (
                      <tr key={`${h.effective_from}-${h.previous}-${h.new}-${i}`} className="border-t">
                        <td className="px-1 py-1">{h.effective_from}</td>
                        <td className="px-1 py-1 tabular-nums">{fmt(h.previous)}</td>
                        <td className="px-1 py-1 tabular-nums font-medium">{fmt(h.new)}</td>
                        <td className="px-1 py-1">{h.reason || '—'}</td>
                        <td className="px-1 py-1">{h.changed_by || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setEditStaff(null)}>Cancel</Button>
              <Button type="submit" data-testid="profile-save-btn">Save Profile</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm" data-testid="payroll-pay-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mark Payroll as Paid</DialogTitle>
            <DialogDescription>Posts salary expenses to Finance → Expenses (once only) and locks the payroll. Net payable: {t && fmt(t.net_salary)}</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Payment Date</Label>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-date-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={() => { act(() => axios.post(`${API}/api/payroll/runs/${run.id}/pay`, { payment_date: payDate }), 'Payroll paid — expenses posted to Finance'); setPayOpen(false); }} data-testid="pay-confirm-btn">Confirm Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
